import {
  CalendarClock,
  CornerDownLeft,
  FolderClosed,
  LayoutGrid,
  Search,
  Settings as SettingsIcon,
  Users,
  Users2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDeals } from "../lib/hooks";
import { cn } from "../lib/cn";
import { Avatar } from "./ui/Avatar";

interface Item {
  id: string;
  group: string;
  label: string;
  sublabel?: string;
  icon: ReactNode;
  keywords: string;
  run: () => void;
}

/** Custom event the Topbar's search button dispatches to open the palette. */
export const CMDK_EVENT = "biedradar:cmdk";

/**
 * Global ⌘K / Ctrl+K command palette: fuzzy-jump to any deal/client or app page,
 * full keyboard nav. Mounted once in the AppShell; also opens from the Topbar's
 * search button (via the CMDK_EVENT window event).
 */
export function CommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: deals } = useDeals();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener(CMDK_EVENT, onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(CMDK_EVENT, onOpen);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setActive(0);
    const id = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(id);
  }, [open]);

  const pages: Item[] = useMemo(() => {
    const go = (to: string) => () => navigate(to);
    const g = t("cmdk.pages");
    return [
      { id: "p-overview", group: g, label: t("nav.overview"), icon: <LayoutGrid size={16} />, keywords: "overview overzicht dashboard home", run: go("/app") },
      { id: "p-clients", group: g, label: t("nav.clients"), icon: <Users size={16} />, keywords: "clients klanten deals pipeline", run: go("/app/clients") },
      { id: "p-agenda", group: g, label: t("nav.agenda"), icon: <CalendarClock size={16} />, keywords: "agenda viewings bezichtigingen calendar", run: go("/app/agenda") },
      { id: "p-docs", group: g, label: t("nav.documents"), icon: <FolderClosed size={16} />, keywords: "documents documenten files", run: go("/app/documents") },
      { id: "p-team", group: g, label: t("nav.team"), icon: <Users2 size={16} />, keywords: "team members leden", run: go("/app/team") },
      { id: "p-settings", group: g, label: t("nav.settings"), icon: <SettingsIcon size={16} />, keywords: "settings instellingen", run: go("/app/settings") },
    ];
  }, [navigate, t]);

  const dealItems: Item[] = useMemo(
    () =>
      (deals ?? []).map((d) => ({
        id: `d-${d.deal.id}`,
        group: t("cmdk.deals"),
        label: d.client.name,
        sublabel: d.property?.address ?? d.deal.title,
        icon: <Avatar name={d.client.name} email={d.client.email} size={22} />,
        keywords: `${d.client.name} ${d.property?.address ?? ""} ${d.deal.title}`,
        run: () => navigate(`/app/deals/${d.deal.id}`),
      })),
    [deals, navigate, t],
  );

  const filtered = useMemo(() => {
    const all = [...pages, ...dealItems];
    const term = q.trim().toLowerCase();
    if (!term) return all;
    return all.filter((it) => `${it.label} ${it.sublabel ?? ""} ${it.keywords}`.toLowerCase().includes(term));
  }, [pages, dealItems, q]);

  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  // Keep the highlighted row in view as the user arrows through.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const grouped = useMemo(() => {
    const map = new Map<string, { item: Item; idx: number }[]>();
    filtered.forEach((item, idx) => {
      const arr = map.get(item.group) ?? [];
      arr.push({ item, idx });
      map.set(item.group, arr);
    });
    return [...map.entries()];
  }, [filtered]);

  if (!open) return null;

  const runAt = (i: number) => {
    const it = filtered[i];
    if (it) {
      it.run();
      setOpen(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 px-4 pt-[12vh] backdrop-blur-sm"
      onMouseDown={() => setOpen(false)}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-xl animate-scale-in overflow-hidden rounded-xl border border-line bg-surface-2 shadow-pop"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 border-b border-line px-4">
          <Search size={17} className="shrink-0 text-ink-faint" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, filtered.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === "Enter") {
                e.preventDefault();
                runAt(active);
              } else if (e.key === "Escape") {
                setOpen(false);
              }
            }}
            placeholder={t("cmdk.placeholder")}
            className="h-12 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-faint"
          />
          <kbd className="hidden rounded border border-line bg-surface-1 px-1.5 py-0.5 text-[10px] text-ink-faint sm:block">Esc</kbd>
        </div>

        <div ref={listRef} className="max-h-[50vh] overflow-auto p-2">
          {filtered.length === 0 ? (
            <p className="py-10 text-center text-sm text-ink-faint">{t("cmdk.empty")}</p>
          ) : (
            grouped.map(([group, rows]) => (
              <div key={group} className="mb-1.5">
                <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">{group}</div>
                {rows.map(({ item, idx }) => (
                  <button
                    key={item.id}
                    data-idx={idx}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => runAt(idx)}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition-colors",
                      idx === active ? "bg-surface-hover" : "hover:bg-surface-3",
                    )}
                  >
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md border border-line bg-surface-1 text-ink-muted">
                      {item.icon}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink">{item.label}</span>
                      {item.sublabel && <span className="block truncate text-xs text-ink-faint">{item.sublabel}</span>}
                    </span>
                    {idx === active && <CornerDownLeft size={14} className="shrink-0 text-ink-faint" />}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-line px-3 py-2 text-[11px] text-ink-faint">
          <span className="flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> {t("cmdk.hintNavigate")}</span>
          <span className="flex items-center gap-1"><Kbd>↵</Kbd> {t("cmdk.hintOpen")}</span>
          <span className="flex items-center gap-1"><Kbd>esc</Kbd> {t("cmdk.hintClose")}</span>
        </div>
      </div>
    </div>
  );
}

function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded border border-line bg-surface-1 px-1 py-0.5 text-[10px] text-ink-faint">{children}</kbd>;
}
